/**
 * Проигрывание записанного вопроса — замена `audioplayers` и
 * `DeviceFileSource` из Flutter-приложения.
 *
 * Контроллер чата держит один плеер на весь диалог и трижды обращается к
 * нему: `setSource(file)`, `resume()` по нажатию на пузырь своего
 * сообщения и `stop()`. Во Flutter источником был путь к файлу во
 * временной папке; в браузере файла на диске нет, поэтому источник —
 * объектный URL поверх `File`, который отдала запись.
 *
 * Предыдущий URL освобождается при каждой смене источника: иначе записи
 * копятся в памяти вкладки до перезагрузки, а киоск не перезагружается
 * сутками.
 */

export class AudioPlayer {
  #element: HTMLAudioElement | null = null;
  #objectUrl: string | null = null;
  #disposed = false;

  /**
   * Задаёт источник и ждёт готовности данных.
   *
   * `setSource` в audioplayers завершается, когда источник действительно
   * подготовлен, — здесь так же: ошибка файла должна долететь до
   * вызывающего, он показывает тост `FailureOp.playback`.
   */
  async setSource(file: File): Promise<void> {
    this.#assertNotDisposed();
    const element = this.#ensureElement();
    element.pause();
    this.#revokeObjectUrl();

    const url = URL.createObjectURL(file);
    this.#objectUrl = url;
    element.src = url;
    element.load();
    await waitForReady(element);
  }

  /** Воспроизведение с текущей позиции — `_player.resume()` в Dart. */
  async resume(): Promise<void> {
    this.#assertNotDisposed();
    const element = this.#element;
    if (element === null) {
      throw new Error('AudioPlayer: источник не задан');
    }
    await element.play();
  }

  /**
   * Останавливает воспроизведение.
   *
   * `stop()` в audioplayers сбрасывает позицию в начало, `pause()` —
   * нет, поэтому позицию обнуляем сами.
   */
  stop(): Promise<void> {
    const element = this.#element;
    if (element !== null) {
      element.pause();
      element.currentTime = 0;
    }
    return Promise.resolve();
  }

  /** Освобождение: соответствует `_player.dispose()` в контроллере. */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;

    const element = this.#element;
    if (element !== null) {
      element.pause();
      element.removeAttribute('src');
      // Перезагрузка пустого источника обрывает сетевую активность
      // элемента — иначе он продолжает держать освобождённый URL.
      element.load();
    }
    this.#revokeObjectUrl();
    this.#element = null;
  }

  #ensureElement(): HTMLAudioElement {
    const existing = this.#element;
    if (existing !== null) return existing;
    const element = new Audio();
    element.preload = 'auto';
    this.#element = element;
    return element;
  }

  #revokeObjectUrl(): void {
    const url = this.#objectUrl;
    if (url === null) return;
    this.#objectUrl = null;
    URL.revokeObjectURL(url);
  }

  #assertNotDisposed(): void {
    if (this.#disposed) {
      throw new Error('AudioPlayer: обращение после dispose()');
    }
  }
}

/** Событие успешной подготовки данных. */
const READY_EVENT = 'canplay';

/**
 * События неудачи. `abort` — это прерывание загрузки чужой рукой:
 * `dispose()` или новый `setSource()` во время подготовки. Без него
 * ожидание никогда бы не завершилось.
 */
const FAILURE_EVENTS = ['error', 'abort'] as const;

/** Готовность данных: первое из событий `canplay`, `error` или `abort`. */
function waitForReady(element: HTMLAudioElement): Promise<void> {
  if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    const cleanup = (): void => {
      element.removeEventListener(READY_EVENT, onReady);
      for (const name of FAILURE_EVENTS) {
        element.removeEventListener(name, onFailure);
      }
    };
    const onReady = (): void => {
      cleanup();
      resolve();
    };
    const onFailure = (): void => {
      cleanup();
      reject(new Error(describeMediaError(element.error)));
    };
    element.addEventListener(READY_EVENT, onReady);
    for (const name of FAILURE_EVENTS) {
      element.addEventListener(name, onFailure);
    }
  });
}

/** Событие `error` само по себе пустое — подробности лежат в `element.error`. */
function describeMediaError(error: MediaError | null): string {
  const details =
    error === null
      ? 'причина неизвестна'
      : `код ${String(error.code)}: ${error.message}`;
  return `AudioPlayer: не удалось подготовить запись (${details})`;
}
